import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { BucketKey, CostLine } from '@fleet/shared';
import { BUCKETS, allMonths, catToBucket, euro, monthLabel } from '@fleet/shared';
import { Dropdown } from '../../components/ui/Dropdown';
import type { DropdownOption } from '../../components/ui/Dropdown';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Panel } from '../../components/ui/Panel';

const chip = (on: boolean): CSSProperties => ({
  background: on ? '#2A6085' : '#FFFFFF',
  color: on ? '#FFFFFF' : '#2A6085',
  border: `1px solid ${on ? '#2A6085' : '#C9BBA8'}`,
  borderRadius: 2,
  padding: '5px 11px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Roboto, sans-serif',
});

/** Dropdown options for the months, grouped by year (newest year first). */
function monthOptions(months: string[]): DropdownOption[] {
  const groups: Record<string, string[]> = {};
  for (const m of months) (groups[m.slice(0, 4)] ??= []).push(m);
  return Object.entries(groups)
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .flatMap(([year, ms]) => ms.map((m) => ({ value: m, label: monthLabel(m), group: year })));
}

/** Per-person cost breakdown: total + per-category bars, filterable by period. */
export function PersoonKosten({ costLines }: { costLines: CostLine[] }) {
  // null = alle, 'YTD' = lopend jaar, anders een 'YYYY-MM'.
  const [filter, setFilter] = useState<string | null>(null);
  const months = allMonths(costLines);
  const ytdYear = months.length ? months[months.length - 1]!.slice(0, 4) : null;

  const filtered =
    filter === null
      ? costLines
      : filter === 'YTD'
        ? ytdYear
          ? costLines.filter((l) => l.maand.startsWith(`${ytdYear}-`))
          : costLines
        : costLines.filter((l) => l.maand === filter);
  const periodeLabel =
    filter === null
      ? 'hele periode'
      : filter === 'YTD'
        ? `dit jaar (${ytdYear})`
        : monthLabel(filter);
  const perBucket = new Map<BucketKey, number>();
  for (const l of filtered) {
    const b = catToBucket(l.categorie);
    perBucket.set(b, (perBucket.get(b) ?? 0) + l.bedragExcl);
  }
  const totaal = filtered.reduce((s, l) => s + l.bedragExcl, 0);
  const max = Math.max(1, ...BUCKETS.map((b) => perBucket.get(b.key) ?? 0));
  const rows = BUCKETS.filter((b) => (perBucket.get(b.key) ?? 0) > 0);

  return (
    <Panel>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Eyebrow>Kosten</Eyebrow>
          <div style={{ fontSize: 26, fontWeight: 900, color: '#2A6085', marginTop: 4 }}>
            {euro(totaal)}
          </div>
          <div style={{ fontSize: 12, color: '#888888' }}>{periodeLabel} · excl. btw</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" style={chip(filter === null)} onClick={() => setFilter(null)}>
            Alle
          </button>
          {ytdYear && (
            <button type="button" style={chip(filter === 'YTD')} onClick={() => setFilter('YTD')}>
              Dit jaar
            </button>
          )}
          {months.length > 0 && (
            <Dropdown
              value={filter && filter !== 'YTD' ? filter : ''}
              onChange={(v) => setFilter(v || null)}
              placeholder="Maand…"
              size="sm"
              ariaLabel="Filter op maand"
              options={monthOptions(months)}
            />
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
        {rows.length === 0 && (
          <div style={{ fontSize: 13, color: '#888888' }}>Geen kosten in deze periode.</div>
        )}
        {rows.map((b) => {
          const v = perBucket.get(b.key) ?? 0;
          return (
            <div
              key={b.key}
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr 90px',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    background: b.color,
                    flex: 'none',
                  }}
                />
                {b.label}
              </span>
              <div
                style={{ background: '#F0E8DC', height: 18, borderRadius: 3, overflow: 'hidden' }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.max(2, Math.round((v / max) * 100))}%`,
                    background: b.color,
                    animation: 'vsGrowW .5s cubic-bezier(.4,0,.2,1) both',
                  }}
                />
              </div>
              <span style={{ fontSize: 13, textAlign: 'right', fontWeight: 700 }}>{euro(v)}</span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
